/**
 * Match Pop Visibility Report Composable
 * 处理页面可见性变化和登录状态变化时的 matchPop 上报
 *
 * 职责：
 * 1. 监听用户登录状态，登录时调用 matchPopReport
 * 2. 监听页面可见性，从后台回到前台时调用 matchPopReport
 * 3. 控制调用间隔（最小 2 分钟），避免频繁请求
 */

import { watch, ref } from 'vue'
import { useUserStore } from '@/stores/user'
import { useGlobalStore } from '@/stores/global'
import { matchPopReport } from '@/api/video'
import { storeToRefs } from 'pinia'

const MIN_INTERVAL = 2 * 60 * 1000 // 2分钟（毫秒）

export function useMatchPopVisibilityReport() {

  const userStore = useUserStore()
  const globalStore = useGlobalStore()
  const { isLoggedIn } = storeToRefs(userStore)
  const { lastMatchPopTime, isDocumentVisible, userStateInitializing } = storeToRefs(globalStore)

  // 标记是否已完成首次初始化后的调用
  const hasCalledAfterInit = ref(false)

  const shouldCallMatchPop = () => {

    // 如果正在初始化用户状态，则延迟调用（避免在初始化期间调用）
    if (globalStore.userStateInitializing) {
      return false
    }

    // 未登录直接返回
    if (!isLoggedIn.value) {
      return false
    }

    const now = Date.now()
    const timeSinceLastCall = now - lastMatchPopTime.value

    // 如果距离上次调用超过2分钟，则返回 true
    if (lastMatchPopTime.value && timeSinceLastCall < MIN_INTERVAL) {
      return false
    }
    return true
  }

  /**
   * 调用 matchPopReport 获取弹窗
   * @returns {Promise<void>}
   */
  async function callMatchPop() {
    matchPopReport({
      status: matchPopReport.STATUS.START,
      action: matchPopReport.ACTION.DEFAULT,
      scene: matchPopReport.SCENE.DEFAULT,
    }, {
      hideLoading: true,
      hideError: true, // 静默调用，失败不显示错误
    })

    // 更新上次调用时间
    globalStore.setLastMatchPopTime(Date.now())
  }

  // 监听页面可见性变化
  watch(isDocumentVisible, (newVal) => newVal && shouldCallMatchPop() && callMatchPop(), { immediate: true })

  // 监听登录状态变化（从未登录变为登录），立即执行一次，不需要时间的判断
  watch(isLoggedIn, (newVal, oldVal) => {
    // 只在登录状态从 false 变为 true 时调用（真正的登录行为）
    if (newVal && !oldVal) {
      callMatchPop()
    }
  }, { immediate: true })
}
